import { useEffect, useMemo, useRef, useState } from "preact/hooks";
import type { JSX } from "preact";
import { Monogram } from "../components/Monogram";
import { Rule } from "../components/Rule";
import {
  ApiError,
  fetchOwnInvite,
  forgetParty,
  sendOwnRsvp,
  type Invite,
} from "../lib/api";
import { day, rsvp as copy, venue } from "../content/wedding";

/**
 * One invitation, one reply — or as many replies as it takes.
 *
 * Everyone named on the invitation answers on this one form, so a couple never
 * has to fill it in twice. Answering for all of them at once is not required:
 * a household can say yes for the two of them today and come back for the
 * third when they know, and anyone left blank stays *awaiting* rather than
 * being recorded as declining. That is the difference the form is careful
 * about — an unanswered guest and a guest who is not coming are not the same
 * person to a caterer.
 *
 * A plus one belongs to a person rather than to the envelope. Each named guest
 * either has one on offer or does not, and the checkbox appears under that
 * guest, after they have said they are coming.
 *
 * There is one way to arrive: the guest found their invitation by name and
 * postcode on `/rsvp`, and the Worker is holding a short-lived party cookie
 * for them — see `RsvpFind`. The cookie is HttpOnly and nothing in a response
 * body identifies the party, not even inside a guest id, so this component
 * never learns anything it could hand on: the guests are numbered by position,
 * and every endpoint it calls reads the cookie instead. There used to be a
 * second way, a bespoke link the invitation carried as a QR, and it is gone.
 */

type Props = {
  /** Already fetched by `RsvpFind`, so arriving does not refetch. */
  initial?: Invite;
  /** The party cookie has run out mid-form; the parent shows the box again. */
  onExpired?: () => void;
};

/** One named guest's line on the form, plus whoever they are bringing. */
type Answer = {
  attending: boolean | null;
  dietary: string;
  bringing: boolean;
  plusName: string;
  plusDietary: string;
};
type Answers = Record<string, Answer>;

const BLANK: Answer = {
  attending: null,
  dietary: "",
  bringing: false,
  plusName: "",
  plusDietary: "",
};

type Phase = "loading" | "ready" | "unreachable";

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

/** "Cheri, Cord and Spencer" — for prose, so the Oxford comma stays out of it. */
function list(names: string[], fallback = "you"): string {
  if (names.length === 0) return fallback;
  if (names.length === 1) return names[0]!;
  return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
}

/*
function firstNames(guests: Invite["guests"]): string {
  return list(
    guests
      .filter((g) => !g.isPlusOne)
      .map((g) => g.name.split(" ")[0] ?? g.name),
  );
}*/

function repliedOn(iso: string): string {
  const when = new Date(iso);
  if (Number.isNaN(when.getTime())) return "";
  return when.toLocaleDateString(undefined, { day: "numeric", month: "long" });
}

export function Rsvp({ initial, onExpired }: Props) {
  const [phase, setPhase] = useState<Phase>(initial ? "ready" : "loading");
  const [invite, setInvite] = useState<Invite | null>(initial ?? null);

  const [answers, setAnswers] = useState<Answers>({});
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [status, setStatus] = useState<"idle" | "sending" | "sent">("idle");
  const [failure, setFailure] = useState("");
  /** Bots fill every field they find; nobody else can see this one. */
  const honeypot = useRef<HTMLInputElement>(null);

  /** Fills the form from whatever the server already holds for this party. */
  function hydrate(next: Invite) {
    setInvite(next);
    // A claimed plus one is a guest row of its own, pointing at whoever
    // brought them; on the form it is two fields under that person's name.
    const brought = new Map(
      next.guests
        .filter((guest) => guest.isPlusOne && guest.hostId)
        .map((guest) => [guest.hostId!, guest]),
    );
    setAnswers(
      Object.fromEntries(
        next.guests
          .filter((guest) => !guest.isPlusOne)
          .map((guest) => {
            const plus = brought.get(guest.id);
            return [
              guest.id,
              {
                attending: guest.attending,
                dietary: guest.dietary,
                bringing: Boolean(plus),
                plusName: plus?.name ?? "",
                plusDietary: plus?.dietary ?? "",
              },
            ];
          }),
      ),
    );
    setEmail(next.contactEmail);
    setMessage(next.message);
  }

  useEffect(() => {
    if (initial) {
      hydrate(initial);
      setPhase("ready");
      return;
    }
    const controller = new AbortController();
    setPhase("loading");
    fetchOwnInvite(controller.signal)
      .then((next) => {
        hydrate(next);
        setPhase("ready");
      })
      .catch((error: unknown) => {
        if (error instanceof DOMException && error.name === "AbortError")
          return;
        // A pass that has run out is not a missing invitation — the guest is
        // still who they said they were, they just have to say it again.
        if (error instanceof ApiError && error.status === 401) {
          onExpired?.();
          return;
        }
        setPhase("unreachable");
      });
    return () => controller.abort();
  }, [initial]);

  const named = useMemo(
    () => (invite?.guests ?? []).filter((guest) => !guest.isPlusOne),
    [invite],
  );
  const anyoneComing = named.some(
    (guest) => answers[guest.id]?.attending === true,
  );
  const answeredCount = named.filter(
    (guest) => answers[guest.id]?.attending != null,
  ).length;

  function setAnswer(id: string, patch: Partial<Answer>) {
    setAnswers((current) => ({
      ...current,
      [id]: { ...BLANK, ...current[id], ...patch },
    }));
  }

  /**
   * Only what would be wrong however long they thought about it. Leaving
   * someone blank is not an error — it is the whole point — so the only floor
   * is that a submission has to say *something* about *somebody*.
   */
  function validate(): Record<string, string> {
    const found: Record<string, string> = {};
    if (answeredCount === 0)
      found.party = "Please answer for at least one of you";
    for (const guest of named) {
      const answer = answers[guest.id];
      if (answer?.bringing && answer.plusName.trim().length < 2) {
        found[guest.id] = "Please tell us your guest’s name";
      }
    }
    if (!EMAIL.test(email.trim()))
      found.email = "Please provide a valid email address";
    return found;
  }

  async function onSubmit(event: JSX.TargetedEvent<HTMLFormElement, Event>) {
    event.preventDefault();
    if (!invite || status === "sending") return;

    const found = validate();
    setErrors(found);
    if (Object.keys(found).length > 0) {
      document
        .querySelector<HTMLElement>(".is-invalid")
        ?.scrollIntoView({ block: "center", behavior: "smooth" });
      return;
    }
    if (honeypot.current?.value) {
      // Silently accept — no feedback for whatever filled that in.
      setStatus("sent");
      return;
    }

    setStatus("sending");
    setFailure("");
    // Everyone is sent, including the ones still undecided: `attending: null`
    // is a real answer to give, and it is how somebody gets taken back off the
    // list after a change of plan.
    const body = {
      guests: named.map((guest) => {
        const answer = answers[guest.id] ?? BLANK;
        return {
          id: guest.id,
          attending: answer.attending,
          dietary: answer.dietary.trim(),
          plusOne:
            guest.plusOneAllowed && answer.attending === true && answer.bringing
              ? {
                  name: answer.plusName.trim(),
                  dietary: answer.plusDietary.trim(),
                }
              : null,
        };
      }),
      email: email.trim(),
      message: message.trim(),
    };

    try {
      // The endpoint reads the cookie. Nothing here has anything else to send.
      const saved = await sendOwnRsvp(body);
      hydrate(saved);
      setStatus("sent");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (error) {
      setStatus("idle");
      if (error instanceof ApiError && error.status === 401) {
        onExpired?.();
        return;
      }
      setFailure(
        error instanceof ApiError
          ? error.message
          : "Something went wrong. Please try again.",
      );
    }
  }

  if (phase === "loading") {
    return (
      <main class="rsvp rsvp--centred">
        <div class="shell notice" aria-live="polite">
          <Monogram class="notice__mark" />
          <p class="notice__wait">Finding your invitation…</p>
        </div>
      </main>
    );
  }

  // The only way to arrive here without an invitation: the pass was good and
  // the request still did not land. A 401 is handled above, by sending the
  // guest back to the name box rather than to this.
  if (phase === "unreachable") {
    return (
      <main class="rsvp rsvp--centred">
        <div class="shell notice">
          <Monogram class="notice__mark" />
          <h1>We could not reach the server</h1>
          <p>
            Something is wrong at our end rather than yours. Please try again in
            a minute.
          </p>
          <a class="btn btn--onForest" href="/">
            Back to the beginning
          </a>
        </div>
      </main>
    );
  }

  if (!invite) return null;

  const closed = !invite.open;

  if (status === "sent") {
    // Who the invitation is still waiting on. Naming them is the whole reason
    // a partial reply is allowed: it turns "we will get back to you" into a
    // thing the guest can see they still owe us.
    const outstanding = named
      .filter((guest) => answers[guest.id]?.attending == null)
      .map((guest) => guest.name.split(" ")[0] ?? guest.name);
    const until = copy.deadline.replace(/^by /, "");

    return (
      <main class="rsvp rsvp--centred">
        <div class="shell notice">
          <Monogram class="notice__mark" />
          <h1>{anyoneComing ? "Wonderful." : "Thank you for telling us."}</h1>
          <p>
            {anyoneComing
              ? `You are on the list. We will write closer to the day with everything you need — ${venue.name}, ${venue.place}, ${day.dateShort}.`
              : "We will miss you, and we are glad to know. There will be a drink raised in your name."}
          </p>
          {outstanding.length > 0 && (
            <p>
              We have not heard about {list(outstanding)} yet — no rush. Come
              back to this page whenever you know and add them.
            </p>
          )}
          <p class="notice__aside">
            {`Change of plans? Look your name up again any time until ${until} and amend anything.`}
          </p>
          <div class="notice__actions">
            <button
              class="btn btn--onForest"
              type="button"
              onClick={() => setStatus("idle")}
            >
              Change our reply
            </button>
            <a class="notice__back" href="/">
              Back to the beginning
            </a>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main class="rsvp">
      <div class="shell">
        <div class="rsvp__head">
          <span class="eyebrow">{copy.eyebrow}</span>
          <h1>{invite.displayName}</h1>
          <Rule class="hero__rule" style={{ marginInline: "auto" }} />
          <p class="rsvp__deadline">
            {invite.respondedAt
              ? `You replied on ${repliedOn(invite.respondedAt)}.${closed ? "" : " Change anything you like below."}`
              : copy.deadline.charAt(0).toUpperCase() + copy.deadline.slice(1)}
          </p>
        </div>

        {closed && (
          <p class="form__status form__status--lede" role="status">
            Replies have closed, so this is how we have you down. If something
            has changed, please send us a message and we will sort it out.
          </p>
        )}

        <form class="form" onSubmit={onSubmit} noValidate>
          <fieldset class="party" disabled={closed}>
            <legend class="field__label party__legend">
              {named.length > 1 ? "Who is coming?" : "Will you be there?"}
            </legend>

            {named.length > 1 && !closed && (
              <p class="party__hint">
                Answer for as many of you as you can — anyone you are unsure
                about can be left blank, and you can come back and finish when
                you know. Clicking an answer again clears it.
              </p>
            )}
            {errors.party && (
              <p class="field__error is-invalid" role="alert">
                {errors.party}
              </p>
            )}

            {named.map((guest) => {
              const answer = answers[guest.id];
              const invalid = Boolean(errors[guest.id]);
              const first = guest.name.split(" ")[0] ?? guest.name;
              return (
                <div
                  key={guest.id}
                  class={`party__guest ${invalid ? "is-invalid" : ""}`}
                >
                  <p class="party__name">{guest.name}</p>
                  <div
                    class="party__choice"
                    role="group"
                    aria-label={guest.name}
                  >
                    {/* Clicking the chosen one again clears it, which a radio
                        does not do by itself. Without that an answer given by
                        accident is permanent, and "still deciding" — a real
                        state here, and the one a partial reply leaves people
                        in — would be reachable only by never having touched
                        the row. `onClick` fires on a checked radio where
                        `onChange` does not, so the two split the work. */}
                    <label class="party__opt">
                      <input
                        type="radio"
                        name={`attending-${guest.id}`}
                        checked={answer?.attending === true}
                        onClick={() => {
                          if (answer?.attending === true)
                            setAnswer(guest.id, { attending: null });
                        }}
                        onChange={() =>
                          setAnswer(guest.id, { attending: true })
                        }
                      />
                      <span>Joyfully accepts</span>
                    </label>
                    <label class="party__opt">
                      <input
                        type="radio"
                        name={`attending-${guest.id}`}
                        checked={answer?.attending === false}
                        onClick={() => {
                          if (answer?.attending === false)
                            setAnswer(guest.id, { attending: null });
                        }}
                        onChange={() =>
                          setAnswer(guest.id, { attending: false, dietary: "" })
                        }
                      />
                      <span>Regretfully declines</span>
                    </label>
                  </div>

                  {answer?.attending === true && (
                    <label class="party__diet">
                      <span class="party__dietLabel">For the kitchen</span>
                      <input
                        type="text"
                        value={answer.dietary}
                        maxLength={300}
                        placeholder={`Allergies or preferences for ${first}?`}
                        aria-label={`Dietary requirements for ${guest.name}`}
                        onInput={(e) =>
                          setAnswer(guest.id, {
                            dietary: e.currentTarget.value,
                          })
                        }
                      />
                    </label>
                  )}

                  {/* Offered to this person rather than to the envelope, and
                      only once they have said they are coming — nobody brings
                      a guest to a wedding they are not at. */}
                  {guest.plusOneAllowed && answer?.attending === true && (
                    <div class="party__plus">
                      <label class="check">
                        <input
                          type="checkbox"
                          checked={answer.bringing}
                          onChange={(e) =>
                            setAnswer(guest.id, {
                              bringing: e.currentTarget.checked,
                            })
                          }
                        />
                        <span class="check__box" aria-hidden="true" />
                        <span class="check__text">
                          <strong>
                            {" "}
                            {named.length > 1
                              ? `${first} is bringing a guest`
                              : `I am bringing a guest`}
                          </strong>
                          <small>
                            {named.length > 1
                              ? "Their invitation includes a plus one"
                              : "Your invitation includes a plus one"}
                          </small>
                        </span>
                      </label>

                      {answer.bringing && (
                        <div class="party__plusFields">
                          <label class="party__diet">
                            <span class="party__dietLabel">Their name</span>
                            <input
                              type="text"
                              value={answer.plusName}
                              maxLength={80}
                              placeholder="So we can write a place card"
                              aria-label={`Name of ${first}’s guest`}
                              onInput={(e) =>
                                setAnswer(guest.id, {
                                  plusName: e.currentTarget.value,
                                })
                              }
                            />
                          </label>
                          <label class="party__diet">
                            <span class="party__dietLabel">
                              For the kitchen
                            </span>
                            <input
                              type="text"
                              value={answer.plusDietary}
                              maxLength={300}
                              placeholder="Allergies or preferences?"
                              aria-label={`Dietary requirements for ${first}’s guest`}
                              onInput={(e) =>
                                setAnswer(guest.id, {
                                  plusDietary: e.currentTarget.value,
                                })
                              }
                            />
                          </label>
                        </div>
                      )}
                    </div>
                  )}

                  {invalid && <p class="field__error">{errors[guest.id]}</p>}
                </div>
              );
            })}
          </fieldset>

          <fieldset class="form__rest" disabled={closed}>
            <div
              class={`field ${errors.email ? "field--invalid is-invalid" : ""}`}
            >
              <label class="field__label" for="email">
                Email <span>*</span>
              </label>
              <input
                id="email"
                name="email"
                type="email"
                inputMode="email"
                autocomplete="email"
                required
                value={email}
                maxLength={254}
                placeholder="So we can send you the details"
                aria-invalid={errors.email ? "true" : undefined}
                aria-describedby={errors.email ? "email-error" : undefined}
                onInput={(e) => setEmail(e.currentTarget.value)}
              />
              {errors.email && (
                <p class="field__error" id="email-error">
                  {errors.email}
                </p>
              )}
            </div>

            <div class="field">
              <label class="field__label" for="message">
                A note for us
              </label>
              <textarea
                id="message"
                name="message"
                rows={3}
                value={message}
                maxLength={1200}
                placeholder="Optional, and always read"
                onInput={(e) => setMessage(e.currentTarget.value)}
              />
            </div>
          </fieldset>

          {/* Honeypot — off-screen, unlabelled, ignored by password managers. */}
          <input
            ref={honeypot}
            type="text"
            name="website"
            tabIndex={-1}
            autocomplete="off"
            class="visually-hidden"
            aria-hidden="true"
          />

          {failure && (
            <p class="form__status form__status--error" role="alert">
              {failure}
            </p>
          )}

          {!closed && (
            <div class="form__actions">
              <button
                class="btn btn--onForest"
                type="submit"
                disabled={status === "sending"}
              >
                {status === "sending"
                  ? "Sending…"
                  : invite.respondedAt
                    ? "Update our reply"
                    : "Send our reply"}
              </button>
              <p class="form__note">
                You can change this any time before replies close.
              </p>
              <button
                class="notice__back rsvp__notYou"
                type="button"
                aria-label={`Not ${invite.displayName}? Search for a different invitation`}
                onClick={() => {
                  // Best effort: the pass expires by itself either way.
                  void forgetParty().catch(() => undefined);
                  onExpired?.();
                }}
              >
                Not you? Search again
              </button>
            </div>
          )}
        </form>
      </div>
    </main>
  );
}
